/**
 * SEO Component
 * =============
 * Manages all SEO-related meta tags and structured data for each page.
 * Uses React Helmet-style approach with useEffect for updating head elements.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  updatePageSEO,
  injectStructuredData,
  removeStructuredData,
  generateBreadcrumbs
} from '../utils/seoHelpers';
import {
  getBreadcrumbSchema,
  getLocalBusinessSchema,
  getWebsiteSchema,
  getVideoSchema
} from '../config/seoData';

/**
 * SEO Component for managing page meta tags and structured data
 * @param {Object} props - Component props
 * @param {Object} props.seoData - SEO data object containing title, description, etc.
 * @param {Array} props.structuredData - Array of structured data objects to inject
 * @param {string} props.pageName - Name of the current page for breadcrumbs
 */
const SEO = ({ seoData, structuredData = [], pageName = 'Home' }) => {
  const location = useLocation();
  const baseUrl = 'https://gupshupchitchatchai.com';

  useEffect(() => {
    // Update all SEO meta tags
    if (seoData) {
      updatePageSEO(seoData);
    }

    // Generate and inject breadcrumb schema
    const breadcrumbs = generateBreadcrumbs(
      pageName,
      `${baseUrl}${location.pathname}`,
      baseUrl
    );
    injectStructuredData(getBreadcrumbSchema(breadcrumbs), 'breadcrumb-schema');

    // Inject page-specific structured data
    structuredData.forEach((data, index) => {
      injectStructuredData(data, `structured-data-${index}`);
    });

    // Cleanup function to remove structured data on unmount
    return () => {
      removeStructuredData('breadcrumb-schema');
      structuredData.forEach((_, index) => {
        removeStructuredData(`structured-data-${index}`);
      });
    };
  }, [seoData, structuredData, pageName, location.pathname]);

  // This component doesn't render anything visible
  return null;
};

/**
 * HomeSEO - SEO component specifically for the homepage
 * Note: Restaurant, Organization, and FAQ schemas are already in index.html (static)
 * Only includes LocalBusiness and Website schemas here to avoid duplicates
 */
export const HomeSEO = ({ seoData }) => {
  const structuredData = [
    getLocalBusinessSchema(),
    getWebsiteSchema(),
    getVideoSchema()
  ];

  return <SEO seoData={seoData} structuredData={structuredData} pageName="Home" />;
};

/**
 * PageSEO - Generic SEO component for standard pages
 */
export const PageSEO = ({ seoData, pageName, additionalSchemas = [] }) => {
  return (
    <SEO
      seoData={seoData}
      structuredData={additionalSchemas}
      pageName={pageName}
    />
  );
};

export default SEO;
